/**
 * YMQ Price Update Script
 * Updates product price when YMQ options are selected
 */
(function() {
  'use strict';

  function formatPrice(cents) {
    // Use Shopify's money format from the HTML attribute
    const moneyFormat = document.documentElement.getAttribute('xo-money-format') || '{{amount}}';
    const moneyWithCurrencyFormat = document.documentElement.getAttribute('xo-money-with-currency-format') || '{{amount}}';
    
    // Check if currency code is enabled (look for money_with_currency in the original price display)
    const hasCurrencyCode = document.querySelector('.xo-price__item')?.textContent?.includes('€') || 
                            document.querySelector('.xo-price__item')?.textContent?.includes('$');
    const format = hasCurrencyCode ? moneyWithCurrencyFormat : moneyFormat;
    
    const amount = cents / 100;
    const value = amount.toFixed(2);
    const valueWithCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/(\d+)\.(\d{2})$/, '$1,$2');
    const valueNoDecimals = Math.round(amount).toString();
    const valueNoDecimalsWithCommas = Math.round(amount).toLocaleString();
    const valueWithSpaceSeparator = value.replace('.', ' ');

    // Replace all Shopify money format placeholders in order
    let formatted = format;
    formatted = formatted.replace(/\{\{amount_with_comma_separator\}\}/g, valueWithCommas);
    formatted = formatted.replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, valueNoDecimalsWithCommas);
    formatted = formatted.replace(/\{\{amount_with_space_separator\}\}/g, valueWithSpaceSeparator);
    formatted = formatted.replace(/\{\{amount_no_decimals\}\}/g, valueNoDecimals);
    formatted = formatted.replace(/\{\{amount\}\}/g, value);
    
    return formatted;
  }

  function getYMQPriceAdditions() {
    let totalAddition = 0;
    
    // Find all property inputs (YMQ options)
    const propertyInputs = document.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
    
    propertyInputs.forEach(input => {
      const name = input.name || '';
      let value = input.value || '';
      
      // Check if input is checked (for checkboxes/radios) or has a value
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!input.checked) return;
      }
      
      if (!value) return;
      
      // Look for YMQ price-related hidden inputs or data attributes
      // YMQ might store price in a hidden input with _ymq_price in the name
      if (name.toLowerCase().includes('_ymq') && name.toLowerCase().includes('price')) {
        // Try to extract numeric value
        const cleanValue = value.toString().replace(/[€$,\s]/g, '').trim();
        const numericValue = parseFloat(cleanValue) || 0;
        
        if (numericValue > 0) {
          // If value looks like currency (has decimal or is small), convert to cents
          if (cleanValue.includes('.') || numericValue < 1000) {
            totalAddition += Math.round(numericValue * 100);
          } else {
            // Assume already in cents
            totalAddition += Math.round(numericValue);
          }
        }
      }
      
      // Check for data attributes on the input that might contain price
      const priceData = input.getAttribute('data-price') || 
                       input.getAttribute('data-ymq-price') ||
                       input.getAttribute('data-addon-price') ||
                       input.closest('label')?.getAttribute('data-price') ||
                       input.closest('.ymq-option')?.getAttribute('data-price');
      
      if (priceData) {
        const priceValue = parseFloat(priceData) || 0;
        if (priceValue > 0) {
          totalAddition += priceValue < 1000 ? Math.round(priceValue * 100) : Math.round(priceValue);
        }
      }
      
      // Also check the label or parent element for price information
      const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
      if (label) {
        const labelText = label.textContent || '';
        // Look for price in label text like "+€100" or "+100€"
        const priceMatch = labelText.match(/[+\-]?\s*€?\s*(\d+(?:[.,]\d{2})?)/);
        if (priceMatch) {
          const priceStr = priceMatch[1].replace(',', '.');
          const priceValue = parseFloat(priceStr) || 0;
          if (priceValue > 0) {
            totalAddition += Math.round(priceValue * 100);
          }
        }
      }
    });
    
    // Also check for any hidden inputs that YMQ might create
    const hiddenInputs = document.querySelectorAll('input[type="hidden"][name*="ymq"], input[type="hidden"][name*="price"]');
    hiddenInputs.forEach(input => {
      const name = (input.name || '').toLowerCase();
      const value = input.value || '';
      
      if (name.includes('ymq') && name.includes('price')) {
        const cleanValue = value.toString().replace(/[€$,\s]/g, '').trim();
        const numericValue = parseFloat(cleanValue) || 0;
        
        if (numericValue > 0) {
          totalAddition += cleanValue.includes('.') || numericValue < 1000 
            ? Math.round(numericValue * 100) 
            : Math.round(numericValue);
        }
      }
    });
    
    return totalAddition;
  }

  // Store base prices for price elements (captured on initial load)
  const basePrices = new Map();
  let basePricesCaptured = false;

  function captureBasePrices() {
    if (basePricesCaptured) return;
    
    // Find all price elements
    const priceSelectors = [
      '.xo-price[data-base-price]',
      '.xo-price',
      '[class*="price"]:not([class*="unit-price"]):not([class*="compare"])',
      '.product-price',
      '.price'
    ];
    
    let priceElements = [];
    for (const selector of priceSelectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        priceElements = Array.from(elements);
        break;
      }
    }
    
    priceElements.forEach(priceEl => {
      // Try to get from data attribute first
      let basePrice = parseInt(priceEl.getAttribute('data-base-price')) || 0;
      let compareAtPrice = parseInt(priceEl.getAttribute('data-compare-at-price')) || 0;
      
      // If no data attribute, extract from displayed price
      if (basePrice === 0) {
        const priceDisplay = priceEl.querySelector('[data-price-display]') || 
                            priceEl.querySelector('.xo-price__item:not(.xo-price__item--del)') ||
                            priceEl.querySelector('.price-current, .product-price-current') ||
                            priceEl;
        
        if (priceDisplay) {
          const priceText = priceDisplay.textContent || '';
          // Try to extract price from text
          const priceMatch = priceText.match(/(\d+(?:[.,]\d{2})?)/);
          if (priceMatch) {
            const priceStr = priceMatch[1].replace(',', '.');
            basePrice = Math.round(parseFloat(priceStr) * 100);
          }
        }
      }
      
      // Store for future use
      if (basePrice > 0) {
        basePrices.set(priceEl, { basePrice, compareAtPrice });
      }
    });
    
    basePricesCaptured = true;
  }

  function getBasePrice(priceEl) {
    // Make sure we've captured base prices
    if (!basePricesCaptured) {
      captureBasePrices();
    }
    
    // Return stored base price or try to get it
    if (basePrices.has(priceEl)) {
      return basePrices.get(priceEl);
    }
    
    // Fallback: try to get from data attribute
    const basePrice = parseInt(priceEl.getAttribute('data-base-price')) || 0;
    const compareAtPrice = parseInt(priceEl.getAttribute('data-compare-at-price')) || 0;
    
    return { basePrice, compareAtPrice };
  }

  function updateProductPrice() {
    // Find all price elements - try multiple selectors for different templates
    const priceSelectors = [
      '.xo-price[data-base-price]',
      '.xo-price',
      '[class*="price"]:not([class*="unit-price"]):not([class*="compare"])',
      '.product-price',
      '.price'
    ];
    
    let priceElements = [];
    for (const selector of priceSelectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        priceElements = Array.from(elements);
        break;
      }
    }
    
    // If still no elements, look for elements containing price text
    if (priceElements.length === 0) {
      const allElements = document.querySelectorAll('[class*="price"], [id*="price"]');
      priceElements = Array.from(allElements).filter(el => {
        const text = el.textContent || '';
        return text.match(/[€$]\s*\d/) && !el.closest('.cart') && !el.closest('.mini-cart');
      });
    }
    
    priceElements.forEach(priceEl => {
      const { basePrice, compareAtPrice } = getBasePrice(priceEl);
      
      if (basePrice === 0) {
        // Can't determine base price, skip
        return;
      }
      
      // Get YMQ price additions
      const ymqAddition = getYMQPriceAdditions();
      const totalPrice = basePrice + ymqAddition;
      // Try multiple selectors to find the price display element
      const priceDisplaySelectors = [
        '[data-price-display]',
        '.xo-price__item:not(.xo-price__item--del)',
        '.price-current',
        '.product-price-current',
        'span:not(.xo-price__item--del)',
        'div:first-child'
      ];
      
      let priceDisplay = null;
      for (const selector of priceDisplaySelectors) {
        priceDisplay = priceEl.querySelector(selector);
        if (priceDisplay && priceDisplay.textContent && priceDisplay.textContent.match(/\d/)) {
          break;
        }
      }
      
      if (priceDisplay) {
        priceDisplay.textContent = formatPrice(totalPrice);
      } else {
        // Fallback: update the first text node or the element itself
        const firstTextNode = Array.from(priceEl.childNodes).find(node => 
          node.nodeType === 3 && node.textContent.match(/\d/)
        );
        if (firstTextNode) {
          firstTextNode.textContent = formatPrice(totalPrice);
        } else if (priceEl.textContent) {
          // Last resort: replace the entire content
          const originalText = priceEl.textContent;
          const priceMatch = originalText.match(/([€$]?\s*)(\d+(?:[.,]\d{2})?)/);
          if (priceMatch) {
            priceEl.textContent = originalText.replace(priceMatch[0], formatPrice(totalPrice));
          }
        }
      }
      
      // Update compare at price if it exists
      const comparePriceDisplay = priceEl.querySelector('[data-compare-price-display]') ||
                                  priceEl.querySelector('.xo-price__item--del') ||
                                  priceEl.querySelector('.price-compare');
      if (comparePriceDisplay && compareAtPrice > 0) {
        comparePriceDisplay.textContent = formatPrice(compareAtPrice + ymqAddition);
      }
    });
  }

  function init() {
    // Listen for changes on property inputs (YMQ options)
    const handleUpdate = (e) => {
      const target = e.target;
      // Check if it's a property input or any input/select that might be YMQ related
      if (target && (
        (target.name && target.name.startsWith('properties[')) ||
        target.closest('[class*="ymq"]') ||
        target.closest('[id*="ymq"]') ||
        target.hasAttribute('data-ymq-price') ||
        target.hasAttribute('data-price')
      )) {
        // Small delay to ensure YMQ has updated any hidden fields
        setTimeout(updateProductPrice, 200);
      }
    };

    // Listen for various events (use bubble phase to avoid interfering with YMQ)
    document.addEventListener('change', handleUpdate, false);
    document.addEventListener('input', handleUpdate, false);
    document.addEventListener('click', (e) => {
      // Only handle clicks on radio/checkbox inputs, not all clicks
      if (e.target.type === 'radio' || e.target.type === 'checkbox') {
        handleUpdate(e);
      }
    }, false);
    
    // Also listen on the product form specifically
    const productForm = document.querySelector('form[action*="/cart/add"], form[id*="product"]');
    if (productForm) {
      productForm.addEventListener('change', handleUpdate, true);
      productForm.addEventListener('click', handleUpdate, true);
    }

    // Listen for custom YMQ events if they exist
    if (window.YMQ) {
      document.addEventListener('ymq:option:changed', updateProductPrice);
      document.addEventListener('ymq:price:updated', updateProductPrice);
    }
    
    // Use MutationObserver to watch for DOM changes (YMQ might add/remove elements)
    const observer = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'checked' || mutation.attributeName === 'value')) {
          shouldUpdate = true;
        }
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 && (
              node.matches && (
                node.matches('input[name*="properties"]') ||
                node.matches('[class*="ymq"]') ||
                node.matches('[id*="ymq"]')
              )
            )) {
              shouldUpdate = true;
            }
          });
        }
      });
      if (shouldUpdate) {
        setTimeout(updateProductPrice, 200);
      }
    });
    
    // Observe the product form or product container
    const productContainer = document.querySelector('form[action*="/cart/add"], [class*="product"], [id*="product"]') || document.body;
    observer.observe(productContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'value', 'data-price', 'data-ymq-price']
    });

    // Capture base prices first, then update
    setTimeout(() => {
      captureBasePrices();
      updateProductPrice();
    }, 500);
    // Also update after a longer delay in case YMQ loads asynchronously
    setTimeout(() => {
      captureBasePrices();
      updateProductPrice();
    }, 2000);
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

